import { modals } from '@mantine/modals';
import { useCallback } from 'react';
import { Text } from '@mantine/core';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import type { MessageId } from '@/assets/lang/en-US';
import modalClasses from './Modals.module.css';
import { useSmallScreen } from '@/hooks/useWindowDimension';

interface BaseConfirmOptions {
  titleId?: MessageId;
  primaryLabelId?: MessageId;
  secondaryLabelId?: MessageId;
  contentVariables?: Record<string, string | number>;
  variant?: 'filled' | 'light';
  danger?: boolean;
  actions?: ('accept' | 'cancel')[];
}

interface ConfirmOptionsWithContentId extends BaseConfirmOptions {
  contentId: MessageId;
  children?: never;
}

interface ConfirmOptionsWithChildren extends BaseConfirmOptions {
  children: React.ReactNode;
  contentId?: never;
}

export type ConfirmOptions =
  | ConfirmOptionsWithContentId
  | ConfirmOptionsWithChildren;

export interface ConfirmResult {
  confirmed: boolean;
}

/**
 * Hook that uses Mantine's built-in modals system for confirmations
 *
 * Usage with contentId (for internationalized text):
 * const confirm = useConfirm();
 *
 * const handleDelete = async () => {
 *   const result = await confirm({
 *     titleId: 'customList.deleteConfirmationTitle',
 *     contentId: 'customList.deleteConfirmationMessage',
 *     primaryLabelId: 'action.delete',
 *     secondaryLabelId: 'action.cancel',
 *     variant: 'filled',
 *     danger: true
 *   });
 *
 *   if (result.confirmed) {
 *     // Proceed with deletion
 *   }
 * };
 *
 * Usage with children (for custom content):
 * const result = await confirm({
 *   titleId: 'customList.deleteConfirmationTitle',
 *   children: <div>Custom content with <strong>formatting</strong></div>,
 *   primaryLabelId: 'action.delete',
 *   secondaryLabelId: 'action.cancel',
 *   variant: 'filled',
 *   danger: true
 * });
 */
export const useConfirm = () => {
  const intl = useClientTerminology();
  const isSmallScreen = useSmallScreen();

  const confirm = useCallback(
    ({
      actions = ['accept', 'cancel'],
      ...options
    }: ConfirmOptions): Promise<ConfirmResult> => {
      const title = options.titleId
        ? intl.formatMessage({ id: options.titleId }, options.contentVariables)
        : '';
      const primaryLabel =
        (options.primaryLabelId
          ? intl.formatMessage({ id: options.primaryLabelId })
          : '') ||
        intl.formatMessage({ id: 'action.confirm' }) ||
        'Confirm';
      const secondaryLabel =
        (options.secondaryLabelId
          ? intl.formatMessage({ id: options.secondaryLabelId })
          : '') ||
        intl.formatMessage({ id: 'action.cancel' }) ||
        'Cancel';

      // Determine content based on which option is provided
      const content =
        'contentId' in options && options.contentId ? (
          <Text size="sm">
            {intl.formatMessage(
              { id: options.contentId },
              options.contentVariables
            )}
          </Text>
        ) : (
          options.children
        );

      const commonButtonProps = {
        radius: 'md',
        fw: 'normal',
        w: isSmallScreen ? '100%' : 'auto',
      };

      return new Promise((resolve) => {
        modals.openConfirmModal({
          title,
          children: content,
          id: 'confirm-modal',
          labels: {
            confirm: primaryLabel,
            cancel: secondaryLabel,
          },
          confirmProps: {
            ...commonButtonProps,
            color: options.danger ? 'redBrand' : undefined,
            id: 'confirm-button',
            variant: options.variant || 'light',
            display: actions.includes('accept') ? 'block' : 'none',
          },
          classNames: {
            root: modalClasses.modalRoot,
            close: modalClasses.modalClose,
            title: modalClasses.modalTitle,
            content: modalClasses.modalContent,
          },
          cancelProps: {
            ...commonButtonProps,
            variant: 'subtle',
            color: 'tealBrand',
            display: actions.includes('cancel') ? 'block' : 'none',
          },
          onConfirm: () => resolve({ confirmed: true }),
          onCancel: () => resolve({ confirmed: false }),
          centered: true,
          withCloseButton: false,
          closeOnClickOutside: false,
        });
      });
    },
    [intl, isSmallScreen]
  );

  return confirm;
};
